import React, { FC, Fragment, useEffect, useState } from 'react';

import { Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Switch } from '@mui/material';

import styles from './UserDetailsDialog.module.scss';
import { UserService } from '../UserService';
import ConfirmationDialog from 'dialogs/ConfirmationDialog/ConfirmationDialog';
import { DetailedUserModel } from 'models/DetailedUserModel';
import { BaseUserModel } from 'models/BaseUserModel';
import StringList from 'components/StringList/StringList';
import KeyValueList, { KeyValue } from 'components/KeyValueList/KeyValueList';
import ManagedUsersTable from './ManagedUsersTable/ManagedUsersTable';
import { AlertConfigModel } from 'dialogs/AlertDialog/AlertConfigModel';
import AlertDialog from 'dialogs/AlertDialog/AlertDialog';

export interface UserDetailsDialogProps {
  onClose: () => void;
  user: BaseUserModel,
}

export const UserDetailsDialog: FC<UserDetailsDialogProps> = (
  {
    onClose,
    user
  }
) => {

  const [currentAlert, setCurrentAlert] = useState<AlertConfigModel | null>(null);
  const [detailedUser, setDetailedUser] = useState<DetailedUserModel | null>();
  const [isAlertDialogOpen, setIsAlertDialogOpen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isResetPasswordConfirmationDialogOpen, setIsResetPasswordConfirmationDialogOpen] = useState<boolean>(false);
  const [personalDetails, setPersonalDetails] = useState<KeyValue[]>([])
  const [personalPortfolios, setPersonalPortfolios] = useState<Record<string, string>>({});
  const [portfolios, setPortfolios] = useState<Record<string, string>>({});
  const [powerplants, setPowerplants] = useState<Record<string, string>>({});

  useEffect(() => {
    setIsLoading(true);

    if (!user?.email) {
      console.warn('Skipping user full info API call due to lacking email/selection');
      setIsLoading(false);
      return;
    }

    setPersonalDetails([
      { key: 'Email', value: user.email },
      { key: 'First Name', value: user.firstName},
      { key: 'Last Name', value: user.lastName }
    ]);

    getDetailedUser();
  }, [user]);

  const cancelPasswordResetHandler = (): void => {
    setIsResetPasswordConfirmationDialogOpen(false);
  };

  const confirmPasswordResetHandler = async (): Promise<void> => {
    setIsResetPasswordConfirmationDialogOpen(false);

    try {
      const resetPasswordResult = await UserService.resetPassword(user.email);

      if (!resetPasswordResult) {
        throw new Error('Error resetting user password');
      }

      closeDialogHandler();
    } catch (error) {
      console.error('Failed at resetting password:', error);
      const alertConfig: AlertConfigModel = {title: 'Error', message: 'Failed at resetting password. Please try again, and contact support if the issue persists.'};
      setCurrentAlert(alertConfig);
      setIsAlertDialogOpen(true);
    }
  };

  const clearStates = (): void => {
    setDetailedUser(null);
    setPersonalDetails([]);
    setPersonalPortfolios({});
    setPortfolios({});
    setPowerplants({});
  };

  const closeDialogHandler = (event?: Object, reason?: any): void => {
    if (reason && reason === 'backdropClick') return;

    clearStates();
    onClose();
  };

  const getDetailedUser = async (): Promise<void> => {
    setIsLoading(true);

    try {
      const fetchedDetailedUser: DetailedUserModel = await UserService.getDetailedUser(user.email);

      if (!fetchedDetailedUser) {
        throw new Error('Error fetching user details for:' + user.email);
      }

      setDetailedUser(fetchedDetailedUser);
      setPersonalPortfolios(fetchedDetailedUser.personalPortfolios);
      setPortfolios(fetchedDetailedUser.portfolios);
      setPowerplants(fetchedDetailedUser.powerplants);
    } catch (error) {
      clearStates();
      console.error('Failed getting user details:', error);
      const alertConfig: AlertConfigModel = {title: 'Error', message: `Failed getting user details for ${user.email}. Please refresh, and contact support if the issue persists.`};
      setCurrentAlert(alertConfig);
      setIsAlertDialogOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  const openResetPasswordConfirmationDialogHandler = (): void => {
    setIsResetPasswordConfirmationDialogOpen(true);
  };

  const userUpdateHandler = async (): Promise<void> => {
    await getDetailedUser();
  };

  const userDetailsGrid = (): JSX.Element => {
    return (
      <div className={styles.userDetailsGrid}>

        {/* roles */}
        <section className={styles.userRoles}>
          <StringList
            header="Roles"
            details={user.roles}
          />
        </section>

        {/* personalDetails */}
        <section className={styles.personalDetails}>
          <KeyValueList
            header="Personal Details"
            details={personalDetails}
          />
        </section>

        {/* accessDetails */}
        <section className={styles.accessDetails}>
          <header className={styles.accessDetailsHeader}>Access Details</header>
          <div className={styles.accessDetailsContent}>
            <FormControlLabel
              className={styles.accessDetailsLabel}
              disabled
              label="User Enabled"
              labelPlacement="start"
              control={<Switch checked={user.enabled ?? false} />}
            />
            <FormControlLabel
              className={styles.accessDetailsLabel}
              disabled
              label="Email Verified"
              labelPlacement="start"
              control={<Switch checked={user.emailVerified ?? false} />}
            />
            {/*<FormControlLabel disabled label="2FA" control={<Switch checked={!!data.twoFactor}></Switch>} />*/}
          </div>
          <footer className={styles.accessDetailsFooter}></footer>
        </section>

        {/* Portfolios (admin created) where the user is a manager */}
        <section className={styles.managedPortfolios}>
          <StringList
            header="Managed Portfolios"
            details={Object.values(portfolios)}
          />
        </section>


        {/* Powerplants in the portfolios (admin created) where the user is a manager */}
        <section className={styles.managedPowerplants}>
          <div className={styles.stringListWrapper}>
            <StringList
              header="Managed Powerplants"
              details={Object.values(powerplants)}
            />
          </div>
        </section>

        {/* Powerplants in the user's personal portfolio */}
        <section className={styles.personalPortfolio}>
          <StringList
            header="Personal Portfolio Powerplants"
            details={Object.values(personalPortfolios)}
          />
        </section>

        {/* Personal portfolios(emails) the user is managing */}
        <section className={styles.managedUsers}>
          { detailedUser &&
            <ManagedUsersTable
              detailedUser={detailedUser}
              isLoading={isLoading}
              onUserUpdate={userUpdateHandler}
            />
          }
        </section>
      </div>
    );
  };

  return (
    <Fragment>
      <Dialog
        className={styles.UserDetailsDialog}
        open={true}
        fullWidth
        maxWidth={'xl'}
        onClose={closeDialogHandler}
        scroll={'body'}
      >
        <DialogTitle>
          User Details

          <Button
            style={{ float: 'right' }}
            variant={'contained'}
            onClick={openResetPasswordConfirmationDialogHandler}
          >
            Reset User Password
          </Button>

          <ConfirmationDialog
            open={isResetPasswordConfirmationDialogOpen}
            title="Reset User Password"
            message="Are you sure you want to reset the password?"
            onConfirm={confirmPasswordResetHandler}
            onCancel={cancelPasswordResetHandler}
          />
        </DialogTitle>

        <DialogContent
          dividers={true}
          className={styles.userDetailsContent}
        >
          {userDetailsGrid()}
        </DialogContent>

        <DialogActions className={styles.userDetailsActions}>
          <Button
            onClick={closeDialogHandler}
            variant="contained"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      { isAlertDialogOpen && currentAlert &&
        <AlertDialog
          onClose={() => {
            setIsAlertDialogOpen(false);
            setCurrentAlert(null);
          }}
          title={currentAlert.title}
          message={currentAlert.message}
        ></AlertDialog>
      }
    </Fragment>
  );
}
